<template>
  <RouterLink
    :to="{ name: routeName, params }"
    v-slot="{ isActive, route }"
    class="group block py-0.25"
    @mouseenter="showLabelTooltip = true"
    @mouseleave="showLabelTooltip = false"
  >
    <div
      class="ease-slow-spatial rounded-medium relative transition-all duration-650 lg:delay-[0ms]"
      :class="[
        type == 'collapsed' && 'h-10',
        (type == 'expanded' || type == 'hidden') && 'h-10',
        type == 'hidden' && '-translate-x-16 opacity-0 lg:delay-[0ms]',
        type != 'hidden' && 'translate-x-0 opacity-100 delay-150 lg:delay-[0ms]',
      ]"
      :style="{ width: contentWidth ? contentWidth + 'px' : 'auto' }"
    >
      <div class="group-hover:bg-on-secondary-container/8 rounded-medium absolute inset-0"></div>

      <div
        class="ease-effect rounded-medium absolute inset-0 -z-10 transition-colors"
        :class="[isRailItemActive(route.path, isActive) && 'bg-secondary-container']"
      ></div>
      <div
        ref="content"
        class="group relative flex h-full w-fit shrink-0 flex-row items-center justify-center px-4"
      >
        <BaseIcon
          v-if="iconName"
          class="ease-effect relative mr-2 transition-colors"
          size="22"
          :class="[
            type == 'collapsed' && 'lg:mr-0',
            isRailItemActive(route.path, isActive) && 'text-on-secondary-container',
          ]"
          :filled="isRailItemActive(route.path, isActive)"
        >
          <div
            v-if="badeCount"
            class="bg-error absolute -top-1 -right-1 grid h-4 w-4 place-items-center rounded-full"
          >
            <span class="text-label-small text-on-error">{{ badeCount }}</span>
          </div>
          {{ iconName }}</BaseIcon
        >
        <BaseMemberAvatar
          v-else-if="member"
          class="ease-effect relative mr-2 transition-colors"
          size="24"
          :class="[
            type == 'collapsed' && 'lg:mr-0',
            isRailItemActive(route.path, isActive) && 'text-on-secondary-container',
          ]"
          :member="member"
          omit-tooltip
        />
        <BaseTeamAvatar
          v-else-if="team"
          class="relative mr-2"
          size="24"
          :class="[type == 'collapsed' && 'lg:mr-0']"
          :name="team.name"
        />
        <Transition
          enter-active-class="ease-spatial duration-650 transition-all"
          enter-from-class="opacity-0 -translate-x-10"
          enter-to-class="opacity-100 translate-x-0"
        >
          <span
            v-if="type == 'expanded'"
            v-html="label"
            class="text-body-medium whitespace-nowrap"
            :class="[isRailItemActive(route.path, isActive) && 'font-medium']"
          />
        </Transition>
        <Teleport to="#menu-container">
          <div
            v-if="type == 'collapsed' && showLabelTooltip"
            :style="{ top: top + 8 + 'px' }"
            class="bg-inverse-surface text-body-small text-on-inverse-surface text-inverse-on-surface rounded-small fixed top-0 left-20 z-60 ml-1 flex h-6 items-center justify-center px-2 whitespace-nowrap"
            v-html="label"
          ></div>
        </Teleport>
      </div>
    </div>
  </RouterLink>
</template>
<script setup lang="ts">
  import { useElementBounding } from '@vueuse/core'
  import { ref, useTemplateRef, watch } from 'vue'
  import BaseIcon from '../base/icon/BaseIcon.vue'
  import { useRoute, type RouteParams } from 'vue-router'
  import BaseMemberAvatar from '../base/avatar/BaseMemberAvatar.vue'
  import BaseTeamAvatar from '../base/avatar/BaseTeamAvatar.vue'
  import type { Member } from '../../models/member/member'
  import type { Team } from '../../models/team/team'

  interface Props {
    routeName: string
    label: string
    iconName?: string
    member?: Member
    team?: Team
    type: 'collapsed' | 'expanded' | 'hidden'
    params?: RouteParams
    badeCount?: number
  }

  const props = defineProps<Props>()
  const currentRoute = useRoute()

  const content = useTemplateRef<HTMLAnchorElement>('content')

  const { width: contentWidth, top, update } = useElementBounding(content)

  watch(
    () => props.type,
    () => {
      setTimeout(() => {
        update()
      }, 400)
    },
  )

  const showLabelTooltip = ref(false)

  const normalizePath = (path: string) => {
    if (path.length <= 1) return path
    return path.replace(/\/+$/, '')
  }

  const isRailItemActive = (targetPath: string, defaultIsActive: boolean) => {
    if (defaultIsActive) return true

    const normalizedCurrentPath = normalizePath(currentRoute.path)
    const normalizedTargetPath = normalizePath(targetPath)

    if (normalizedCurrentPath === normalizedTargetPath) return true

    return normalizedCurrentPath.startsWith(`${normalizedTargetPath}/`)
  }
</script>
